import { useState } from "react";
import { api, money } from "@/lib/api";
import { MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

const STATUS_STYLES = {
  new: "bg-[#2F5D3A]/10 text-[#2F5D3A]",
  nudged: "bg-[#B8860B]/10 text-[#B8860B]",
  converted: "bg-[#1A1A1A]/10 text-[#6E6E69]",
};

const buildMessage = (r) => {
  const restore = `${window.location.origin}/api/cart-reminders/${r.id}/restore`;
  const lines = (r.items || [])
    .map((i) => `- ${i.title}${i.size ? ` (Size ${i.size})` : ""}${i.color ? `, ${i.color}` : ""} x${i.qty}`)
    .join("\n");
  return (
    `Hi! This is Kurti Connection. You left some beautiful pieces in your bag:\n\n${lines}\n\n` +
    `Subtotal: ${money(r.subtotal)}\n\nHere's a link straight back to your bag: ${restore}\n\n` +
    `Any sizing questions? Just reply here!`
  );
};

// /admin/reminders — abandoned bags with one-click WhatsApp nudges.
export default function AdminReminders() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!adminKey) { toast.error("Enter your admin key."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setLoading(true);
    try {
      const { data } = await api.get("/cart-reminders", { headers: { "X-Admin-Key": adminKey } });
      setRows(data.reminders || []);
    } catch {
      toast.error("Could not load — check your admin key.");
      localStorage.removeItem(KEY_STORE);
    }
    setLoading(false);
  };

  const nudge = async (r) => {
    window.open(`https://wa.me/${r.phone}?text=${encodeURIComponent(buildMessage(r))}`, "_blank", "noopener");
    try {
      await api.post(`/cart-reminders/${r.id}/nudge`, {}, { headers: { "X-Admin-Key": adminKey } });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: "nudged" } : x)));
    } catch {
      toast.error("WhatsApp opened, but could not mark as nudged.");
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10 font-body" data-testid="admin-reminders">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl flex items-center gap-2">
          <MessageCircle size={22} className="text-[#1FA855]" /> Abandoned Bag Reminders
        </h1>
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-reminders-key"
            className="border border-[#E5E5DF] px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#B84A3B]"
          />
          <button onClick={load} data-testid="admin-reminders-load"
            className="bg-[#1A1A1A] text-white px-4 py-2 text-[0.7rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] transition-colors flex items-center gap-2">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Load
          </button>
        </div>
      </div>

      {rows && rows.length === 0 && (
        <p className="mt-10 text-[#6E6E69]" data-testid="admin-reminders-empty">No abandoned bags yet.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="mt-6 space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="border border-[#E5E5DF] p-4" data-testid={`reminder-row-${r.id}`}>
              <div className="flex justify-between gap-3 flex-wrap items-start">
                <div>
                  <p className="font-medium text-sm font-mono" data-testid={`reminder-phone-${r.id}`}>+{r.phone}</p>
                  <p className="text-sm text-[#6E6E69] mt-0.5">
                    {(r.items || []).map((i) => i.title).join(", ")} · <b className="text-[#1A1A1A]">{money(r.subtotal)}</b>
                  </p>
                  <p className="text-xs font-mono text-[#6E6E69] mt-1">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className={`text-[0.6rem] font-mono tracking-[0.12em] uppercase px-2.5 py-1 ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}
                    data-testid={`reminder-status-${r.id}`}>
                    {r.status}
                  </span>
                  <button
                    data-testid={`reminder-nudge-${r.id}`}
                    onClick={() => nudge(r)}
                    disabled={r.status === "converted"}
                    className="bg-[#1FA855] text-white px-3.5 py-2 text-[0.65rem] font-mono tracking-[0.12em] uppercase hover:bg-[#17853f] transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <MessageCircle size={13} /> Nudge
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
